import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const API_BASE = '/api/limitmarket';

const DEFAULT_FILTERS = {
  marketTerm: '',
  marketSort: 'liquidity',
  orderSort: 'order-size',
  minLiquidity: '',
  minOrder: '1000',
  maxOrder: '',
  direction: 'all',
  minProb: '',
  maxProb: '',
  maxDistance: '',
  timeWindow: '24h',
  sizeMode: 'maker'
};

const FILTER_KEYS = Object.keys(DEFAULT_FILTERS);
const DIRECTION_OPTIONS = new Set(['all', 'YES', 'NO']);
const TIME_WINDOW_OPTIONS = new Set(['1h', '24h', '7d', '30d', 'all']);
const ORDER_SORT_OPTIONS = new Set(['order-size', 'created-time', 'created-time-asc']);
const MARKET_SORT_OPTIONS = new Set(['liquidity', 'volume', 'created-time', 'last-bet-time']);
const SIZE_MODE_OPTIONS = new Set(['maker', 'taker']);

const TARGET_RESULTS = 40;

const numberFormatter = new Intl.NumberFormat('en-US');

const parseNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatMana = (value) => `M$${numberFormatter.format(Math.round(value))}`;

const formatPercent = (value) => {
  if (value === null || value === undefined) return '--';
  const pct = value * 100;
  return `${pct.toFixed(pct < 1 ? 2 : 1)}%`;
};

const formatDeltaPercent = (limitProb, marketProb) => {
  if (!Number.isFinite(limitProb) || !Number.isFinite(marketProb)) return null;
  const delta = Math.round((limitProb - marketProb) * 100);
  const sign = delta >= 0 ? '+' : delta < 0 ? '-' : '';
  return `${sign}${Math.abs(delta)}%`;
};

const getMarketProbability = (market, answerId) => {
  if (!market) return null;
  if (answerId && Array.isArray(market.answers)) {
    const answer = market.answers.find(item => item.id === answerId);
    if (answer) {
      const answerProb = answer.probability ?? answer.prob ?? answer.p;
      if (Number.isFinite(answerProb)) return answerProb;
    }
  }
  const marketProb = market.probability ?? market.prob ?? market.p;
  return Number.isFinite(marketProb) ? marketProb : null;
};

const getMarketAnswerLabel = (market, answerId) => {
  if (!answerId) return null;
  if (market && Array.isArray(market.answers)) {
    const answer = market.answers.find(item => item.id === answerId);
    if (answer) {
      const answerText = answer.text ?? answer.answer ?? answer.name ?? answer.title ?? answer.label;
      if (answerText) return answerText;
    }
  }
  return String(answerId);
};

const formatTimeAgo = (timestamp) => {
  if (!timestamp) return 'Unknown';
  const diff = Date.now() - timestamp;
  if (diff < 60 * 1000) return 'Just now';
  const minutes = Math.floor(diff / (60 * 1000));
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const getRemainingOrderAmount = (bet) => {
  const total = Number.isFinite(bet.orderAmount) ? bet.orderAmount : null;
  const filled = Number.isFinite(bet.amount) ? bet.amount : 0;
  if (total !== null) return Math.max(total - filled, 0);
  if (Number.isFinite(bet.amount)) return bet.amount;
  return 0;
};

const getTakerOrderAmount = (bet) => {
  const makerAmount = getRemainingOrderAmount(bet);
  const limitProb = bet?.limitProb;
  const direction = bet?.outcome || 'YES';
  if (!Number.isFinite(limitProb)) return null;
  if (direction === 'YES') {
    if (limitProb <= 0 || limitProb >= 1) return null;
    return makerAmount * (1 - limitProb) / limitProb;
  }
  if (direction === 'NO') {
    if (limitProb <= 0 || limitProb >= 1) return null;
    return makerAmount * limitProb / (1 - limitProb);
  }
  return null;
};

const getOrderSizeForSort = (order, sizeMode) => {
  if (sizeMode === 'taker') {
    const takerAmount = getTakerOrderAmount(order);
    return Number.isFinite(takerAmount) ? takerAmount : 0;
  }
  return getRemainingOrderAmount(order);
};

const sortOrders = (orders, sortKey, sizeMode) => {
  const sorted = [...orders];
  switch (sortKey) {
    case 'order-size':
      sorted.sort((a, b) => getOrderSizeForSort(b, sizeMode) - getOrderSizeForSort(a, sizeMode));
      break;
    case 'created-time-asc':
      sorted.sort((a, b) => (a.createdTime || 0) - (b.createdTime || 0));
      break;
    case 'created-time':
    default:
      sorted.sort((a, b) => (b.createdTime || 0) - (a.createdTime || 0));
      break;
  }
  return sorted;
};

const buildAggregateUrl = (filters) => {
  const params = new URLSearchParams();
  const setIfPresent = (key, value) => {
    if (value !== '' && value !== undefined && value !== null) {
      params.set(key, value);
    }
  };
  setIfPresent('marketTerm', filters.marketTerm);
  setIfPresent('marketSort', filters.marketSort);
  setIfPresent('minLiquidity', filters.minLiquidity);
  setIfPresent('minOrder', filters.minOrder);
  setIfPresent('maxOrder', filters.maxOrder);
  setIfPresent('direction', filters.direction);
  setIfPresent('minProb', filters.minProb);
  setIfPresent('maxProb', filters.maxProb);
  setIfPresent('maxDistance', filters.maxDistance);
  setIfPresent('timeWindow', filters.timeWindow);
  setIfPresent('orderSort', filters.orderSort);
  setIfPresent('sizeMode', filters.sizeMode);
  params.set('targetResults', String(TARGET_RESULTS));
  return `${API_BASE}/aggregate?${params.toString()}`;
};

const fetchJson = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Request failed (${response.status})`);
  }
  return response.json();
};

const sanitizeFilters = (filters) => {
  const sanitized = { ...filters };
  if (!DIRECTION_OPTIONS.has(sanitized.direction)) sanitized.direction = DEFAULT_FILTERS.direction;
  if (!TIME_WINDOW_OPTIONS.has(sanitized.timeWindow)) sanitized.timeWindow = DEFAULT_FILTERS.timeWindow;
  if (!ORDER_SORT_OPTIONS.has(sanitized.orderSort)) sanitized.orderSort = DEFAULT_FILTERS.orderSort;
  if (!MARKET_SORT_OPTIONS.has(sanitized.marketSort)) sanitized.marketSort = DEFAULT_FILTERS.marketSort;
  if (!SIZE_MODE_OPTIONS.has(sanitized.sizeMode)) sanitized.sizeMode = DEFAULT_FILTERS.sizeMode;
  return sanitized;
};

const getFiltersFromSearch = () => {
  if (typeof window === 'undefined') return DEFAULT_FILTERS;
  const params = new URLSearchParams(window.location.search);
  const next = { ...DEFAULT_FILTERS };
  FILTER_KEYS.forEach((key) => {
    if (!params.has(key)) return;
    const value = params.get(key);
    if (value !== null) next[key] = value;
  });
  return sanitizeFilters(next);
};

export default function LimitMarketPage() {
  const [filters, setFilters] = useState(() => getFiltersFromSearch());
  const [orders, setOrders] = useState([]);
  const [marketData, setMarketData] = useState({});
  const [userData, setUserData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');
  const requestIdRef = useRef(0);
  const didSyncUrlRef = useRef(false);

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setValidationError('');
  };

  const validateFilters = (nextFilters) => {
    const minOrder = parseNumber(nextFilters.minOrder);
    const maxOrder = parseNumber(nextFilters.maxOrder);
    if (minOrder !== null && maxOrder !== null && minOrder > maxOrder) {
      return 'Minimum order size must be less than the maximum.';
    }
    const minProb = parseNumber(nextFilters.minProb);
    const maxProb = parseNumber(nextFilters.maxProb);
    if (minProb !== null && maxProb !== null && minProb > maxProb) {
      return 'Minimum probability must be less than the maximum.';
    }
    const maxDistance = parseNumber(nextFilters.maxDistance);
    if (maxDistance !== null && maxDistance < 0) {
      return 'Maximum distance must be zero or greater.';
    }
    const minLiquidity = parseNumber(nextFilters.minLiquidity);
    if (minLiquidity !== null && minLiquidity < 0) {
      return 'Minimum liquidity must be zero or greater.';
    }
    return '';
  };

  const loadOrders = useCallback(async (nextFilters) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError('');
    setOrders([]);

    try {
      const url = buildAggregateUrl(nextFilters);
      const data = await fetchJson(url);

      if (requestId !== requestIdRef.current) return;

      setMarketData(data?.markets || {});
      setUserData(data?.users || {});
      const sortedOrders = sortOrders(data?.orders || [], nextFilters.orderSort, nextFilters.sizeMode);
      setOrders(sortedOrders);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError('Unable to load open limit orders. Please try again.');
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const validationMessage = validateFilters(filters);
    setValidationError(validationMessage);
    if (validationMessage) {
      return undefined;
    }

    const handle = setTimeout(() => {
      loadOrders(filters);
    }, 350);

    return () => clearTimeout(handle);
  }, [filters, loadOrders]);

  useEffect(() => {
    if (!didSyncUrlRef.current) {
      didSyncUrlRef.current = true;
    }
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams();
    FILTER_KEYS.forEach((key) => {
      const value = filters[key];
      if (value === undefined || value === null || value === '') return;
      if (String(value) === String(DEFAULT_FILTERS[key])) return;
      params.set(key, String(value));
    });
    const queryString = params.toString();
    const nextUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;
    window.history.replaceState(null, '', nextUrl);
  }, [filters]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handlePopState = () => {
      setFilters(getFiltersFromSearch());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const summaryText = useMemo(() => {
    if (loading) return 'Loading open limit orders...';
    if (orders.length === 0) return 'No open limit orders match your criteria.';
    return `Showing ${orders.length} open limit order${orders.length === 1 ? '' : 's'}.`;
  }, [loading, orders.length]);
  const sizeModeLabel = 'Amount';

  return (
    <div>
      <div className="page-header limit-market-header">
        <div>
          <h1 className="page-title">Manifold Limit Order Marketplace</h1>
          <p className="page-subtitle">Find open limit orders and the markets they support.</p>
        </div>
      </div>

      {validationError && <div className="error-banner">{validationError}</div>}
      {error && <div className="error-banner">{error}</div>}

      <div className="card filter-card">
        <div className="filter-section">
          <div className="filter-row">
            <div className="filter-block">
              <div className="filter-block-title">Amount</div>
              <div className="filter-inline">
                <div className="form-group">
                  <label className="form-label">
                    Taker amounts
                    <span
                      className="info-icon"
                      role="tooltip"
                      aria-label="Taker amount is the cost to fill the order. It equals order_size × (1 - prob) / prob for YES, or order_size × prob / (1 - prob) for NO."
                      data-tooltip="Taker amount is the cost to fill the order. It equals order_size × (1 - prob) / prob for YES, or order_size × prob / (1 - prob) for NO."
                    >
                      i
                    </span>
                  </label>
                  <div className="toggle-group">
                    <input
                      id="taker-toggle"
                      className="toggle-input"
                      type="checkbox"
                      checked={filters.sizeMode === 'taker'}
                      onChange={(event) =>
                        setFilters(prev => ({
                          ...prev,
                          sizeMode: event.target.checked ? 'taker' : 'maker'
                        }))
                      }
                    />
                    <label className="toggle-switch" htmlFor="taker-toggle">
                      <span className="toggle-knob" aria-hidden="true" />
                    </label>
                  </div>
                </div>
                <div className="form-group form-group-compact">
                  <label className="form-label" htmlFor="min-order">Min {sizeModeLabel.toLowerCase()}</label>
                  <input
                    id="min-order"
                    className="form-input"
                    type="number"
                    min="0"
                    placeholder="e.g., 1000"
                    value={filters.minOrder}
                    onChange={(event) => setFilters(prev => ({ ...prev, minOrder: event.target.value }))}
                  />
                </div>
                <div className="form-group form-group-compact">
                  <label className="form-label" htmlFor="max-order">Max {sizeModeLabel.toLowerCase()}</label>
                  <input
                    id="max-order"
                    className="form-input"
                    type="number"
                    min="0"
                    placeholder="No max"
                    value={filters.maxOrder}
                    onChange={(event) => setFilters(prev => ({ ...prev, maxOrder: event.target.value }))}
                  />
                </div>
              </div>
            </div>
            <div className="filter-block">
              <div className="filter-block-title">Other Filters</div>
              <div className="filter-inline">
                <div className="form-group">
                  <label className="form-label" htmlFor="time-window">Time placed</label>
                  <select
                    id="time-window"
                    className="form-select"
                    value={filters.timeWindow}
                    onChange={(event) => setFilters(prev => ({ ...prev, timeWindow: event.target.value }))}
                  >
                    <option value="1h">Last hour</option>
                    <option value="24h">Last 24 hours</option>
                    <option value="7d">Last 7 days</option>
                    <option value="30d">Last 30 days</option>
                    <option value="all">All time</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="order-sort">Order sort</label>
                  <select
                    id="order-sort"
                    className="form-select"
                    value={filters.orderSort}
                    onChange={(event) => setFilters(prev => ({ ...prev, orderSort: event.target.value }))}
                  >
                    <option value="order-size">Order size</option>
                    <option value="created-time">Newest</option>
                    <option value="created-time-asc">Oldest</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <details className="filter-advanced">
          <summary className="filter-summary">Advanced filters</summary>
          <div className="filter-section">
            <div className="filter-section-title">Market criteria</div>
            <div className="filter-grid">
              <div className="form-group">
                <label className="form-label" htmlFor="market-term">Search term</label>
                <input
                  id="market-term"
                  className="form-input"
                  type="text"
                  placeholder="Search markets by title or description"
                  value={filters.marketTerm}
                  onChange={(event) => setFilters(prev => ({ ...prev, marketTerm: event.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="market-sort">Market sort</label>
                <select
                  id="market-sort"
                  className="form-select"
                  value={filters.marketSort}
                  onChange={(event) => setFilters(prev => ({ ...prev, marketSort: event.target.value }))}
                >
                  <option value="liquidity">Liquidity</option>
                  <option value="volume">Volume</option>
                  <option value="created-time">Newest</option>
                  <option value="last-bet-time">Last bet time</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="min-liquidity">Minimum liquidity</label>
                <input
                  id="min-liquidity"
                  className="form-input"
                  type="number"
                  min="0"
                  placeholder="e.g., 500"
                  value={filters.minLiquidity}
                  onChange={(event) => setFilters(prev => ({ ...prev, minLiquidity: event.target.value }))}
                />
              </div>
            </div>
          </div>

          <div className="filter-section">
            <div className="filter-section-title">Order criteria</div>
            <div className="filter-grid">
              <div className="form-group">
                <label className="form-label" htmlFor="direction">Direction</label>
                <select
                  id="direction"
                  className="form-select"
                  value={filters.direction}
                  onChange={(event) => setFilters(prev => ({ ...prev, direction: event.target.value }))}
                >
                  <option value="all">Any</option>
                  <option value="YES">YES</option>
                  <option value="NO">NO</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="min-prob">Minimum probability (%)</label>
                <input
                  id="min-prob"
                  className="form-input"
                  type="number"
                  min="0"
                  max="100"
                  placeholder="e.g., 10"
                  value={filters.minProb}
                  onChange={(event) => setFilters(prev => ({ ...prev, minProb: event.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="max-prob">Maximum probability (%)</label>
                <input
                  id="max-prob"
                  className="form-input"
                  type="number"
                  min="0"
                  max="100"
                  placeholder="e.g., 90"
                  value={filters.maxProb}
                  onChange={(event) => setFilters(prev => ({ ...prev, maxProb: event.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="max-distance">Max distance from current (%)</label>
                <input
                  id="max-distance"
                  className="form-input"
                  type="number"
                  min="0"
                  max="100"
                  placeholder="e.g., 25"
                  value={filters.maxDistance}
                  onChange={(event) => setFilters(prev => ({ ...prev, maxDistance: event.target.value }))}
                />
              </div>
            </div>
          </div>
        </details>

        <div className="limit-market-actions">
          <button type="button" className="btn btn-secondary" onClick={resetFilters}>
            Reset
          </button>
        </div>
      </div>

      <div className="limit-market-toolbar">
        <div className="limit-market-summary">{summaryText}</div>
      </div>

      {loading ? (
        <div className="card">
          <div className="center-message">Fetching open limit orders…</div>
        </div>
      ) : orders.length === 0 ? (
        <div className="card">
          <div className="order-empty">No open limit orders match your criteria.</div>
        </div>
      ) : (
        <div className="orders-list">
          {orders.map(order => {
            const market = marketData[order.contractId];
            const marketTitle = market?.question || 'Loading market details…';
            const marketUrl = market?.url;
            const liquidity = market?.totalLiquidity;
            const volume = market?.volume;
            const marketProbability = getMarketProbability(market, order.answerId);
            const limitDelta = formatDeltaPercent(order.limitProb, marketProbability);
            const answerLabel = getMarketAnswerLabel(market, order.answerId);
            const direction = order.outcome || 'YES';
            const directionClass = direction === 'YES' ? 'yes' : direction === 'NO' ? 'no' : 'neutral';
            const makerAmount = getRemainingOrderAmount(order);
            const takerAmount = getTakerOrderAmount(order);
            const displayAmount = filters.sizeMode === 'taker' ? takerAmount : makerAmount;
            const bettor = userData[order.userId];
            const bettorName = bettor?.username || bettor?.name || 'Unknown';
            const bettorProfileUrl = bettor?.username ? `https://manifold.markets/${bettor.username}` : null;
            const cardClickable = Boolean(marketUrl);
            const cardAriaLabel = cardClickable
              ? `Open market: ${marketTitle}${answerLabel ? ` (${answerLabel})` : ''}`
              : undefined;

            const handleCardClick = () => {
              if (marketUrl) {
                window.open(marketUrl, '_blank', 'noopener,noreferrer');
              }
            };

            const handleCardKeyDown = (event) => {
              if (!cardClickable) return;
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleCardClick();
              }
            };

            return (
              <div
                className={`order-card ${cardClickable ? 'is-clickable' : ''}`}
                key={order.id}
                onClick={handleCardClick}
                onKeyDown={handleCardKeyDown}
                role={cardClickable ? 'link' : undefined}
                tabIndex={cardClickable ? 0 : undefined}
                aria-label={cardAriaLabel}
              >
                <div>
                  <h3 className="order-market-title">
                    {answerLabel && (
                      <span className="order-market-answer">{answerLabel}</span>
                    )}
                    {answerLabel && <span className="order-market-separator"> | </span>}
                    <span className="order-market-question">{marketTitle}</span>
                  </h3>
                  <div className="order-hero">
                    <div className="order-hero-item">
                      <span className="order-hero-label">Direction</span>
                      <span className={`direction-pill ${directionClass}`}>{direction}</span>
                    </div>
                    <div className="order-hero-item">
                      <span className="order-hero-label">Remaining {sizeModeLabel.toLowerCase()}</span>
                      <span className="order-hero-value">
                        {displayAmount !== null ? formatMana(displayAmount) : '--'}
                      </span>
                    </div>
                    <div className="order-hero-item">
                      <span className="order-hero-label">Limit price</span>
                      <span className="order-hero-value">
                        {formatPercent(order.limitProb)}
                        {limitDelta ? (
                          <span className="order-hero-delta">({limitDelta})</span>
                        ) : null}
                      </span>
                    </div>
                    <div className="order-hero-item">
                      <span className="order-hero-label">Placed by</span>
                      {bettorProfileUrl ? (
                        <a
                          className="order-user-link"
                          href={bettorProfileUrl}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {bettorName}
                        </a>
                      ) : (
                        <span className="order-hero-value">{bettorName}</span>
                      )}
                    </div>
                  </div>
                  <div className="order-footer">
                    <span className="order-status">Placed {formatTimeAgo(order.createdTime)}</span>
                  </div>
                </div>
                <div className="order-market-aside">
                  <div className="order-market-meta">
                    <span>Market ID: {order.contractId}</span>
                    {marketProbability !== undefined && (
                      <span>Current price: {formatPercent(marketProbability)}</span>
                    )}
                    {liquidity !== undefined && <span>Liquidity: {formatMana(liquidity)}</span>}
                    {volume !== undefined && <span>Volume: {formatMana(volume)}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
